/** Ligne entrepôt LC (select document) ou pool enrichi avec erp_id. */
export type LcEntrepotRowLike = {
  id?: unknown;
  lc_id?: unknown;
  erp_id?: unknown;
  erp_entrepot_id?: unknown;
  code_entrepot?: unknown;
  code?: unknown;
  nom?: unknown;
  libelle?: unknown;
};

export type ErpEntrepotRowLike = {
  id?: unknown;
  code?: unknown;
  code_entrepot?: unknown;
  nom?: unknown;
  libelle?: unknown;
};

function normalizeEntrepotCode(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function normalizeEntrepotName(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function lcRowId(row: LcEntrepotRowLike): number | null {
  const n = Number(row?.lc_id ?? row?.id);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Id ERP (`erp_main.entrepots`) depuis la sélection document (souvent id LC).
 * Ne renvoie jamais l'id caisse brut si une ligne LC correspond sans mapping ERP.
 */
export function mapLcSelectIdToErpEntrepotId(
  selectedId: unknown,
  lcRows: LcEntrepotRowLike[],
  erpRows: ErpEntrepotRowLike[]
): number | null {
  const n = Number(selectedId);
  if (!Number.isFinite(n) || n <= 0) {
    return null;
  }
  const lcList = Array.isArray(lcRows) ? lcRows : [];
  const erpList = Array.isArray(erpRows) ? erpRows : [];

  const lcRow = lcList.find(
    (x) => Number(x?.id) === n || Number(x?.lc_id) === n
  );
  if (lcRow) {
    const direct = Number(lcRow.erp_id ?? lcRow.erp_entrepot_id);
    if (Number.isFinite(direct) && direct > 0) {
      return direct;
    }
    const code = normalizeEntrepotCode(lcRow.code_entrepot ?? lcRow.code);
    const nom = normalizeEntrepotName(lcRow.libelle ?? lcRow.nom);
    for (const e of erpList) {
      const eid = Number(e?.id);
      if (!Number.isFinite(eid) || eid <= 0) {
        continue;
      }
      const codeE = normalizeEntrepotCode(e.code ?? e.code_entrepot);
      if (code && codeE && code === codeE) {
        return eid;
      }
    }
    for (const e of erpList) {
      const eid = Number(e?.id);
      if (!Number.isFinite(eid) || eid <= 0) {
        continue;
      }
      const nomE = normalizeEntrepotName(e.nom ?? e.libelle);
      if (nom && nomE && nom === nomE) {
        return eid;
      }
    }
    return null;
  }

  if (erpList.some((e) => Number(e?.id) === n)) {
    return n;
  }
  return null;
}

/**
 * Mappe un id ERP vers l'id LC du ng-select via erp_id / erp_entrepot_id uniquement
 * (pas de repli id LC === id ERP).
 */
export function mapErpEntrepotIdToLcSelectId(
  erpEntrepotId: unknown,
  enrichedLcPool: LcEntrepotRowLike[]
): number | null {
  const erp = Number(erpEntrepotId);
  if (!Number.isFinite(erp) || erp <= 0) {
    return null;
  }
  const pool = Array.isArray(enrichedLcPool) ? enrichedLcPool : [];
  const mapped = pool.find(
    (x) => Number(x?.erp_id) === erp || Number(x?.erp_entrepot_id) === erp
  );
  if (mapped) {
    return lcRowId(mapped);
  }
  return mapErpEntrepotIdToLcViaErpTable(erp, pool, []);
}

/** ERP id → LC id via table ERP + correspondance code / libellé. */
export function mapErpEntrepotIdToLcViaErpTable(
  erpId: number,
  lcRows: LcEntrepotRowLike[],
  erpRows: ErpEntrepotRowLike[]
): number | null {
  const erp = Number(erpId);
  if (!Number.isFinite(erp) || erp <= 0) {
    return null;
  }
  const lcList = Array.isArray(lcRows) ? lcRows : [];
  const erpList = Array.isArray(erpRows) ? erpRows : [];
  const erpRow = erpList.find((e) => Number(e?.id) === erp);
  if (!erpRow) {
    return null;
  }
  const codeE = normalizeEntrepotCode(erpRow.code ?? erpRow.code_entrepot);
  const nomE = normalizeEntrepotName(erpRow.nom ?? erpRow.libelle);
  for (const w of lcList) {
    const codeLc = normalizeEntrepotCode(w.code_entrepot ?? w.code);
    if (codeLc && codeE && codeLc === codeE) {
      return lcRowId(w);
    }
  }
  if (nomE) {
    for (const w of lcList) {
      const nomLc = normalizeEntrepotName(w.libelle ?? w.nom);
      if (nomLc && nomLc === nomE) {
        return lcRowId(w);
      }
    }
  }
  return null;
}

/**
 * Résout l'id LC pour affichage / select : hidden LC prioritaire, puis id déjà LC,
 * puis mapping ERP → LC.
 */
export function resolveLcEntrepotIdFromErpOrHidden(
  rawId: unknown,
  hiddenLcId: unknown,
  lcRows: LcEntrepotRowLike[],
  erpRows: ErpEntrepotRowLike[] = []
): number | null {
  const hidden = Number(hiddenLcId);
  if (Number.isFinite(hidden) && hidden > 0) {
    return hidden;
  }
  const raw = Number(rawId);
  if (!Number.isFinite(raw) || raw <= 0) {
    return null;
  }
  const lcList = Array.isArray(lcRows) ? lcRows : [];
  if (lcList.some((w) => Number(w.id) === raw || Number(w.lc_id) === raw)) {
    return raw;
  }
  const fromEnriched = mapErpEntrepotIdToLcSelectId(raw, lcList);
  if (fromEnriched != null) {
    return fromEnriched;
  }
  return mapErpEntrepotIdToLcViaErpTable(raw, lcList, erpRows);
}

/**
 * PV d'écart — destination : `entrepot_dest_id` ERP est souvent déjà un id logiciel caisse.
 * Pas de mapping ERP→LC (évite un mauvais entrepôt).
 */
export function resolvePvEcartDestinationLcId(
  rawDestId: unknown,
  hiddenLcDestId: unknown,
  lcRows: LcEntrepotRowLike[]
): number | null {
  const hidden = Number(hiddenLcDestId);
  if (Number.isFinite(hidden) && hidden > 0) {
    return hidden;
  }
  const raw = Number(rawDestId);
  if (!Number.isFinite(raw) || raw <= 0) {
    return null;
  }
  const lcList = Array.isArray(lcRows) ? lcRows : [];
  if (lcList.some((w) => Number(w.id) === raw || Number(w.lc_id) === raw)) {
    return raw;
  }
  return raw;
}
